import { useState, forwardRef, ElementRef, ForwardedRef } from "react";
import "react-dates/lib/css/_datepicker.css";
import "react-dates/initialize";

import {
  DateRangePicker as ReactDatesDateRangePicker,
  DateRangePickerShape,
} from "react-dates";
import {
  BaseDatePicker,
  Caption,
  createIcon,
  StyledDatePickerContainer,
  NavigationButton,
} from "../common";
import { MonthYearSelector } from "./MonthYearSelector";
import { Icon } from "../Icon";
import { FlattenSimpleInterpolation } from "styled-components";
import moment from "moment";

type DatePickerProps = Omit<
  DateRangePickerShape,
  "startDate" | "endDate" | "onDatesChange" | "isOutsideRange"
> & {
  startDate: Date | null;
  endDate: Date | null;
  onDatesChange?: ({
    startDate,
    endDate,
  }: {
    startDate?: Date;
    endDate?: Date;
  }) => void;
  isOutsideRange?: ((day: Date) => boolean) | undefined;
  caption?: string;
  errorMessage?: string;
  isRequired?: boolean;
  label?: string;
  placeholder?: string;
  showMonthYearSelector?: boolean;
  styles?: FlattenSimpleInterpolation;
};

export const DateRangePicker = forwardRef(
  (
    props: DatePickerProps,
    ref: ForwardedRef<ElementRef<typeof ReactDatesDateRangePicker>>,
  ) => {
    const [focusedInput, setFocusedInput] = useState(props.focusedInput);
    const [startDate, setStartDate] = useState<moment.Moment | null>(
      props.startDate ? moment(props.startDate) : null,
    );
    const [endDate, setEndDate] = useState<moment.Moment | null>(
      props.endDate ? moment(props.endDate) : null,
    );

    return (
      <BaseDatePicker
        disabled={!!props.disabled}
        errorMessage={props.errorMessage}
        isRequired={props.isRequired}
        label={props.label}
        id={props.startDateId}
      >
        <StyledDatePickerContainer
          disabled={!!props.disabled}
          readOnly={props.readOnly}
          hasError={!!props.errorMessage}
          hasSelectors={!!props.showMonthYearSelector}
          styles={props.styles}
        >
          <ReactDatesDateRangePicker
            {...props}
            ref={ref}
            disabled={props.disabled || props.readOnly}
            required={props.isRequired}
            hideKeyboardShortcutsPanel={
              props.hideKeyboardShortcutsPanel ?? true
            }
            customInputIcon={
              props.customInputIcon ??
              createIcon(
                props.disabled ? true : false,
                props.readOnly ? true : false,
                focusedInput ? true : false,
              )
            }
            inputIconPosition={props.inputIconPosition ?? "after"}
            daySize={props.daySize ?? 32}
            navPrev={
              <NavigationButton className="nav-prev">
                <Icon iconSize="small" iconName="chevron-left" plain />
              </NavigationButton>
            }
            navNext={
              <NavigationButton className="nav-next">
                <Icon iconSize="small" iconName="chevron-right" plain />
              </NavigationButton>
            }
            noBorder
            small
            block
            startDate={startDate}
            endDate={endDate}
            focusedInput={focusedInput}
            onDatesChange={({
              startDate,
              endDate,
            }: {
              startDate: moment.Moment | null;
              endDate: moment.Moment | null;
            }) => {
              setStartDate(startDate);
              setEndDate(endDate);
              props.onDatesChange &&
                props.onDatesChange({
                  startDate: startDate?.toDate(),
                  endDate: endDate?.toDate(),
                });
            }}
            isOutsideRange={(day: moment.Moment) => {
              return props.isOutsideRange
                ? props.isOutsideRange(day.toDate())
                : false;
            }}
            onFocusChange={(focusedInput) => {
              setFocusedInput(focusedInput);
            }}
            {...(props.showMonthYearSelector && {
              renderMonthElement: MonthYearSelector,
            })}
            keepOpenOnDateSelect={props.keepOpenOnDateSelect ?? true}
            startDatePlaceholderText={
              props.startDatePlaceholderText ?? props.placeholder
            }
            endDatePlaceholderText={
              props.endDatePlaceholderText ?? props.placeholder
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- there's a bug in @types/react-dates
            renderMonthText={props.renderMonthText as any}
          />
        </StyledDatePickerContainer>
        {props.caption && (
          <Caption
            isDisabled={!!props.disabled || props.readOnly}
            hasError={!!props.errorMessage}
          >
            {props.errorMessage || props.caption}
          </Caption>
        )}
      </BaseDatePicker>
    );
  },
);

DateRangePicker.displayName = "DateRangePicker";
